// Filename: src/components/MapComponent.js
//----------------------------------------
import React, { useEffect, useRef, useCallback, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
// import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import Analytics from '../services/analytics';
import { logFrontendEvent } from '../services/apiLogger';
import { apiConfig } from '../config/apiConfig';
import './MapComponent.css';

// Replace with your actual Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoidmFyaWsiLCJhIjoiY2tlZGQxejhnMGEzczJ6a2ZjdnMxcGo5NyJ9.jCdGWqxlTtpyc_PV0qnuPA';

// List of interesting locations shown when the URL has no geo coords
const RANDOM_LOCATIONS = [
  [103.607576, 1.260082],
  [-3.143646, 51.206135],
  [9.09206, 54.17284],
  [13.793975, 52.391837],
  [35.192924, 28.017638],
  [-97.156017, 25.997236],
  [-3.003483, 53.424515],
  [150.758075, -33.900984],
  [120.94848, 14.796748],
  [73.055907, 18.988538],
  [77.554992, 28.100495],
  [79.85, 6.94275],
  [-1.18413, 54.61495],
  [-70.92121, 41.648122],
  [8.43411, 55.46784],
  [9.15134, 53.89832],
  [11.321493, 54.683214],
  [11.206983, 54.505729],
  [-0.477198, 51.582698],
  [-1.88992, 52.48039],
  [-1.41157, 52.30428],
  [-1.72596, 52.5633],
  [27.13382, 60.58086],
  [-5.49093, 35.89686],
  [71.922063, 30.310683],
  [71.183612, 30.091673],
  [93.955849, 26.525947],
  [92.69629, 26.36901],
  [-114.799358, 36.053021],
  [17.2673, 63.17078],
  [-7.862119, 42.292495],
  [21.57971, 38.02177],
];

const DEFAULT_CENTER = [20.562815, 44.020417];
const DEFAULT_ZOOM_DESKTOP = 15.5;
const MOBILE_MAX_WIDTH = 768;
const DEFAULT_ZOOM_MOBILE = DEFAULT_ZOOM_DESKTOP - 1;
const getDefaultZoom = () =>
  window.innerWidth <= MOBILE_MAX_WIDTH ? DEFAULT_ZOOM_MOBILE : DEFAULT_ZOOM_DESKTOP;

const MapComponent = ({
  setBbox,
  selectedImage,
  mapCenter,
  mapZoom,
  setMapCenter,
  setMapZoom,
  showRedBox,
  setBoxDimensionsForImages, // Add this new prop
  onMapMove,
  onRequestBboxUpdate, // New prop to handle bbox update requests
  onRequestMapState, // New prop to expose current map state
  imageViewMode,
  onToggleImageView,
  tileMode,
  onToggleTileMode,
  mode,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const redBoxRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const imageBoundsRef = useRef(null); // Store the image bounds
  const currentImageRef = useRef(null); // Store current image for proper transitions
  const imageLayerRef = useRef(null); // Keep track of active image layers
  const previousImageViewModeRef = useRef(null); // Track previous image view mode state
  const isMovingToSearchResult = useRef(false); // Flag to prevent infinite loop

  // Store the box dimensions in state
  const [boxDimensions, setBoxDimensions] = useState({
    width: 500, // Default width in meters (changed from 1000m)
    height: 500, // Default height in meters (changed from 1000m)
  });

  // Pass box dimensions to parent component for ImageList
  useEffect(() => {
    if (setBoxDimensionsForImages) {
      setBoxDimensionsForImages(boxDimensions);
    }
  }, [boxDimensions, setBoxDimensionsForImages]);
  
  // Refs for resize functionality
  const resizeHandleRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartPointRef = useRef(null);
  const dragStartBoxRef = useRef(null);

  // Simple function to update red box visual size based on current dimensions
  const updateRedBoxVisual = useCallback(() => {
    if (!map.current || !redBoxRef.current || !showRedBox) return;

    const center = map.current.getCenter();
    const metersPerDegreeLatitude = 111320;
    const metersPerDegreeLongitude = 111320 * Math.cos(center.lat * Math.PI / 180);

    // Convert box dimensions to degrees
    const halfWidthInDegrees = (boxDimensions.width / 2) / metersPerDegreeLongitude;
    const halfHeightInDegrees = (boxDimensions.height / 2) / metersPerDegreeLatitude;

    // Calculate corners
    const west = center.lng - halfWidthInDegrees;
    const east = center.lng + halfWidthInDegrees;
    const south = center.lat - halfHeightInDegrees;
    const north = center.lat + halfHeightInDegrees;

    // Project to pixels
    const northEast = map.current.project([east, north]);
    const southWest = map.current.project([west, south]);
    const pixelWidth = Math.abs(northEast.x - southWest.x);
    const pixelHeight = Math.abs(northEast.y - southWest.y);

    // Update DOM directly
    redBoxRef.current.style.width = `${pixelWidth}px`;
    redBoxRef.current.style.height = `${pixelHeight}px`;
  }, [showRedBox, boxDimensions]);

  // Manual bbox update - only call this when actually needed (e.g., before API calls)
  const updateBboxManually = useCallback(() => {
    if (!map.current || !setBbox) return;
    
    const center = map.current.getCenter();
    const metersPerDegreeLatitude = 111320;
    const metersPerDegreeLongitude = 111320 * Math.cos(center.lat * Math.PI / 180);
    
    const halfWidthInDegrees = (boxDimensions.width / 2) / metersPerDegreeLongitude;
    const halfHeightInDegrees = (boxDimensions.height / 2) / metersPerDegreeLatitude;
    
    const west = center.lng - halfWidthInDegrees;
    const east = center.lng + halfWidthInDegrees;
    const south = center.lat - halfHeightInDegrees;
    const north = center.lat + halfHeightInDegrees;
    
    // Track area selection
    const areaSqm = boxDimensions.width * boxDimensions.height;
    Analytics.trackMapAreaSelected([west, south, east, north], areaSqm);
    
    setBbox([west, south, east, north]);
  }, [setBbox, boxDimensions]);

  // Store handlers and functions in refs to avoid circular dependencies
  const handlersRef = useRef({});
  const functionsRef = useRef({});

  // Update refs when functions change
  useEffect(() => {
    functionsRef.current.updateRedBoxVisual = updateRedBoxVisual;
    functionsRef.current.updateBboxManually = updateBboxManually;
  }, [updateRedBoxVisual, updateBboxManually]);

  const pendingDimensionsRef = useRef(null);

  // Handle resize drag
  const handleResizeDrag = (e) => {
    if (!isDraggingRef.current || !dragStartPointRef.current || !dragStartBoxRef.current || !map.current || !redBoxRef.current) return;

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    // Calculate pixel delta
    const deltaX = clientX - dragStartPointRef.current.x;
    const deltaY = clientY - dragStartPointRef.current.y;

    // Get current map metrics
    const bounds = map.current.getBounds();
    const mapWidth = map.current.getContainer().offsetWidth;
    const mapHeight = map.current.getContainer().offsetHeight;
    const center = map.current.getCenter();

    // Calculate meters per pixel
    const metersPerDegreeLatitude = 111320;
    const metersPerDegreeLongitude = 111320 * Math.cos(center.lat * Math.PI / 180);
    const metersPerPixelLng = (bounds.getEast() - bounds.getWest()) * metersPerDegreeLongitude / mapWidth;
    const metersPerPixelLat = (bounds.getNorth() - bounds.getSouth()) * metersPerDegreeLatitude / mapHeight;

    // Calculate new dimensions
    let newWidth = dragStartBoxRef.current.width + (deltaX * metersPerPixelLng);
    let newHeight = dragStartBoxRef.current.height + (deltaY * metersPerPixelLat);

    // Constraints
    newWidth = Math.max(50, newWidth);
    newHeight = Math.max(50, newHeight);
    const newAreaKm2 = (newWidth * newHeight) / 1000000;

    if (newAreaKm2 <= 2) {
      // Update visual appearance immediately
      const halfWidthInDegrees = (newWidth / 2) / metersPerDegreeLongitude;
      const halfHeightInDegrees = (newHeight / 2) / metersPerDegreeLatitude;
      
      const west = center.lng - halfWidthInDegrees;
      const east = center.lng + halfWidthInDegrees;
      const south = center.lat - halfHeightInDegrees;
      const north = center.lat + halfHeightInDegrees;
      
      const northEast = map.current.project([east, north]);
      const southWest = map.current.project([west, south]);
      
      redBoxRef.current.style.width = `${Math.abs(northEast.x - southWest.x)}px`;
      redBoxRef.current.style.height = `${Math.abs(northEast.y - southWest.y)}px`;
      
      // Store the latest dimensions but don't update state during drag
      pendingDimensionsRef.current = { width: newWidth, height: newHeight };
    }
  };

  // End resize drag
  const endResizeDrag = () => {
    // Remove global event listeners first
    document.removeEventListener('mousemove', handlersRef.current.handleResizeDrag);
    document.removeEventListener('mouseup', handlersRef.current.endResizeDrag);
    document.removeEventListener('touchmove', handlersRef.current.handleResizeDrag);
    document.removeEventListener('touchend', handlersRef.current.endResizeDrag);
    
    // Re-enable transitions
    if (redBoxRef.current) {
      redBoxRef.current.style.transition = 'width 0.15s ease-out, height 0.15s ease-out';
    }
    
    // Reset drag state
    isDraggingRef.current = false;
    dragStartPointRef.current = null;
    dragStartBoxRef.current = null;
    
    // Ensure final dimensions are set if there are pending updates
    if (pendingDimensionsRef.current) {
      const finalDimensions = pendingDimensionsRef.current;
      pendingDimensionsRef.current = null;
      
      // Simply update dimensions - no bbox update needed here
      setBoxDimensions(finalDimensions);
    }
  };

  // Store handlers and current box dimensions in ref
  handlersRef.current.handleResizeDrag = handleResizeDrag;
  handlersRef.current.endResizeDrag = endResizeDrag;
  handlersRef.current.boxDimensions = boxDimensions;

  // Start resize drag
  const startResizeDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    isDraggingRef.current = true;
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    
    dragStartPointRef.current = { x: clientX, y: clientY };
    dragStartBoxRef.current = { ...boxDimensions };
    
    // Remove any transitions during drag
    if (redBoxRef.current) {
      redBoxRef.current.style.transition = 'none';
    }
    
    // Add global event listeners
    const moveEvent = e.type.includes('touch') ? 'touchmove' : 'mousemove';
    const endEvent = e.type.includes('touch') ? 'touchend' : 'mouseup';
    
    document.addEventListener(moveEvent, handlersRef.current.handleResizeDrag, { passive: false });
    document.addEventListener(endEvent, handlersRef.current.endResizeDrag, { passive: false });
  }, [boxDimensions]);

  // Initialize map
  useEffect(() => {
    if (map.current) return; // Initialize map only once

    const hasHash =
      typeof window !== 'undefined' &&
      window.location &&
      window.location.hash &&
      window.location.hash.length > 1;

    const initialCenter = hasHash
      ? DEFAULT_CENTER
      : RANDOM_LOCATIONS[Math.floor(Math.random() * RANDOM_LOCATIONS.length)];

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v11',
      center: initialCenter,
      zoom: getDefaultZoom(),
      hash: true, // Enable the hash functionality
      fadeDuration: 0, // Disable default fade for better control
      refreshExpiredTiles: false, // Prevent unnecessary tile refreshes
      dragRotate: false, // Disable rotation on drag
      keyboard: false, // Disable keyboard rotation shortcuts
      pitchWithRotate: false, // Disable pitch when rotating
      touchRotate: false, // Disable touch rotation on mobile
    });

    map.current.on('load', () => {
      setMapLoaded(true);
      const scale = new mapboxgl.ScaleControl({ maxWidth: 80, unit: 'metric' });
      map.current.addControl(scale, 'bottom-left');

      // Set initial center and zoom only if not already set (e.g., from search)
      if (setMapCenter && !mapCenter) setMapCenter(map.current.getCenter());
      if (setMapZoom && !mapZoom) setMapZoom(map.current.getZoom());
      
      // Initial updates
      updateRedBoxVisual();
      // Set initial bbox once after map loads
      setTimeout(() => {
        if (functionsRef.current.updateBboxManually) {
          functionsRef.current.updateBboxManually();
        }
      }, 100);
    });

    const handleMove = () => {
      // Only update visual, not bbox (to prevent flickering)
      if (!isDraggingRef.current && showRedBox) {
        updateRedBoxVisual();
      }
    };

    const handleMoveEnd = () => {
      // Only update React state if we're not moving to a search result
      // For manual user panning, we don't need to update React state
      // This prevents circular dependencies and unnecessary re-renders
      if (!isMovingToSearchResult.current) {
        // Don't update state for manual user movements to prevent reload issues
        // The map's internal state is sufficient for user interactions
        // React state is only updated for programmatic movements (search, etc.)
      }
    };

    const handleUserMove = () => {
      if (onMapMove) onMapMove();
    };

    map.current.on('move', handleMove);
    map.current.on('moveend', handleMoveEnd);
    map.current.on('dragstart', handleUserMove);
    map.current.on('zoomstart', handleUserMove);

    return () => {
      if (map.current) {
        map.current.off('move', handleMove);
        map.current.off('moveend', handleMoveEnd);
        map.current.off('dragstart', handleUserMove);
        map.current.off('zoomstart', handleUserMove);
        map.current.remove();
        map.current = null; // Important: Set map.current to null after removing
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMapCenter, setMapZoom, onMapMove, updateRedBoxVisual, showRedBox]);
  
  // Expose the manual update function to parent
  useEffect(() => {
    if (onRequestBboxUpdate) {
      onRequestBboxUpdate(() => updateBboxManually);
    }
  }, [onRequestBboxUpdate, updateBboxManually]);

  // Function to get current map center and zoom
  const getCurrentMapState = useCallback(() => {
    if (!map.current) return null;
    return {
      center: map.current.getCenter(),
      zoom: map.current.getZoom()
    };
  }, []);

  // Expose current map state function to parent via prop
  useEffect(() => {
    if (typeof onRequestMapState === 'function') {
      onRequestMapState(getCurrentMapState);
    }
  }, [onRequestMapState, getCurrentMapState]);
  
  // Update visual when map moves or dimensions change
  useEffect(() => {
    if (!map.current || !mapLoaded || isDraggingRef.current || !showRedBox) return;
    updateRedBoxVisual();
  }, [boxDimensions, updateRedBoxVisual, mapLoaded, showRedBox]);

  // Respond to external mapCenter and mapZoom changes (e.g., from search)
  useEffect(() => {
    if (!map.current || !mapLoaded || !mapCenter || !mapZoom) return;
    
    // Prevent infinite loop during search result movement
    if (isMovingToSearchResult.current) return;
    
    console.log('Moving map to search result:', mapCenter, mapZoom);
    isMovingToSearchResult.current = true;
    
    map.current.flyTo({
      center: [mapCenter.lng, mapCenter.lat],
      zoom: mapZoom,
      duration: 1500 // Smooth animation duration
    });
    
    // Reset the flag after animation completes
    setTimeout(() => {
      isMovingToSearchResult.current = false;
    }, 1600); // Slightly longer than animation duration
  }, [mapCenter, mapZoom, mapLoaded]);

  // Commented out built-in geocoder since we're using custom search
  // useEffect(() => {
  //   if (!map.current || !mapLoaded) return;
  //   const geocoder = new MapboxGeocoder({
  //     accessToken: mapboxgl.accessToken,
  //     mapboxgl,
  //     marker: false,
  //   });
  //   map.current.addControl(geocoder, 'top-right');
  //   return () => {
  //     if (map.current) {
  //       map.current.removeControl(geocoder);
  //     }
  //   };
  // }, [mapLoaded]);


  // Ref to track if a click sequence is in progress (prevent multiple simultaneous sequences)
  const clickSequenceInProgressRef = useRef(false);
  const clickTimeoutRefs = useRef([]);

  // Helper function to safely reset click sequence state
  const resetClickSequence = useCallback(() => {
    console.log('Resetting click sequence state');
    clickSequenceInProgressRef.current = false;
    // Clear any pending timeouts
    clickTimeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
    clickTimeoutRefs.current = [];
  }, []);

  // Function to handle image click with different behaviors for S2 vs SR images
  const handleImageClick = useCallback((e) => {
    if (!map.current || !mapLoaded || !imageBoundsRef.current || !selectedImage) return;
    
    // Prevent multiple click sequences from running simultaneously
    if (clickSequenceInProgressRef.current) {
      console.log('Click sequence already in progress, ignoring click');
      return;
    }
    
    // Ensure the current image layer exists before proceeding
    const currentLayerId = imageLayerRef.current && imageLayerRef.current[0];
    if (!currentLayerId || !map.current.getLayer(currentLayerId)) {
      console.log('Current image layer not found, ignoring click');
      return;
    }
    
    const clickLngLat = e.lngLat;
    const bounds = imageBoundsRef.current; // [west, south, east, north]
    // Check if click is within image bounds
    if (
      clickLngLat.lng >= bounds[0] &&
      clickLngLat.lng <= bounds[2] &&
      clickLngLat.lat >= bounds[1] &&
      clickLngLat.lat <= bounds[3]
    ) {
      const isSuperResolved = selectedImage.superresolved === true;
      console.log('Image clicked, superresolved:', selectedImage.superresolved, 'isSuperResolved:', isSuperResolved);
      
      // Mark sequence as in progress
      clickSequenceInProgressRef.current = true;
      
      // Track image comparison
      Analytics.trackImageComparison('compare_click', selectedImage.date);
      
      try {
        // Unified click sequence: previous image for 1s → background map for 1s → current image
        console.log('Image click sequence starting (unified behavior)');
        
        // Step 1: Make current image transparent (showing previous image underneath)
        if (map.current.getLayer(currentLayerId)) {
          map.current.setPaintProperty(currentLayerId, 'raster-opacity', 0);
          console.log('Step 1: Made current image transparent (showing previous image)');
        }
        
        // Step 2: After 0.1 second, hide all layers (show background map)
        const timeout1 = setTimeout(() => {
          try {
            // Hide all image layers to show background map
            if (imageLayerRef.current) {
              imageLayerRef.current.forEach(layerId => {
                if (map.current && map.current.getLayer(layerId)) {
                  map.current.setLayoutProperty(layerId, 'visibility', 'none');
                }
              });
            }
            
            // Also check for any other image layers that might exist
            const layers = map.current.getStyle().layers;
            layers.forEach(layer => {
              if (layer.id.includes('image-layer') || layer.id.startsWith('temp-image-layer-')) {
                map.current.setLayoutProperty(layer.id, 'visibility', 'none');
              }
            });

            // Hide tile layer too during peek
            if (map.current.getLayer('s2-tile-layer')) {
              map.current.setLayoutProperty('s2-tile-layer', 'visibility', 'none');
            }
            
            console.log('Step 2: Hidden all image layers (showing background map)');
          } catch (error) {
            console.error('Error in step 2:', error);
            resetClickSequence();
          }
        }, 100);
        clickTimeoutRefs.current.push(timeout1);
        
        // Step 3: After 1.1 seconds total (0.1s previous + 1s map), restore current image
        const timeout2 = setTimeout(() => {
          try {
            const layerId = imageLayerRef.current && imageLayerRef.current[0];
            if (map.current && layerId && map.current.getLayer(layerId)) {
              map.current.setLayoutProperty(layerId, 'visibility', 'visible');
              map.current.setPaintProperty(layerId, 'raster-opacity', 1);
              console.log('Step 3: Restored current image');
            }
            // Restore tile layer after peek
            if (map.current && map.current.getLayer('s2-tile-layer')) {
              map.current.setLayoutProperty('s2-tile-layer', 'visibility', 'visible');
            }
          } catch (error) {
            console.error('Error in step 3:', error);
          } finally {
            // Always reset sequence state
            resetClickSequence();
          }
        }, 1100);
        clickTimeoutRefs.current.push(timeout2);
      } catch (error) {
        console.error('Error starting click sequence:', error);
        resetClickSequence();
      }
    }
  }, [mapLoaded, selectedImage, resetClickSequence]);

  // Cleanup effect to reset click sequence when component unmounts
  useEffect(() => {
    return () => {
      resetClickSequence();
    };
  }, [resetClickSequence]);

  // Reset click sequence only when selectedImage URL actually changes (not just metadata updates)
  const previousImageUrlRef = useRef(null);
  useEffect(() => {
    if (selectedImage && selectedImage.url !== previousImageUrlRef.current) {
      console.log('Image URL changed, resetting click sequence:', previousImageUrlRef.current, '->', selectedImage.url);
      // Immediate reset - don't wait for async operations
      clickSequenceInProgressRef.current = false;
      clickTimeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      clickTimeoutRefs.current = [];
      console.log('Click sequence immediately reset for new image');
      previousImageUrlRef.current = selectedImage.url;
    }
  }, [selectedImage]);

  // Improved image transition logic
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove previous click handler
    map.current.off('click', handleImageClick);

    // Tile mode: add raster tile source BELOW the classic/SR image overlay
    // Classic overlay renders instantly; tiles extend coverage around it
    // SR image stays on top at full ~1m resolution
    if (tileMode && selectedImage && apiConfig.tileserver) {
      const tileSourceId = 's2-tiles';
      const tileLayerId = 's2-tile-layer';

      const addTileLayer = (cogUrl) => {
        if (!map.current || !cogUrl) return;

        // Remove existing tile source if present
        if (map.current.getSource(tileSourceId)) {
          if (map.current.getLayer(tileLayerId)) map.current.removeLayer(tileLayerId);
          map.current.removeSource(tileSourceId);
        }

        const encodedCogUrl = encodeURIComponent(cogUrl);
        const tileUrl = `${apiConfig.tileserver}/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedCogUrl}&rescale=0,255`;

        map.current.addSource(tileSourceId, {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256,
          minzoom: 7,
          maxzoom: 14,
        });

        // Insert tile layer BELOW any existing image layer so SR stays on top
        const existingImageLayer = imageLayerRef.current && imageLayerRef.current[0];
        const beforeLayer = existingImageLayer && map.current.getLayer(existingImageLayer)
          ? existingImageLayer : undefined;

        map.current.addLayer({
          id: tileLayerId,
          type: 'raster',
          source: tileSourceId,
          paint: { 'raster-opacity': 1, 'raster-fade-duration': 300 },
        }, beforeLayer);
      };

      if (selectedImage.cog_tci_url) {
        addTileLayer(selectedImage.cog_tci_url);
      } else if (selectedImage.path) {
        const resolveUrl = apiConfig.tileserver.replace('/cog', '/resolve');
        fetch(`${resolveUrl}?path=${encodeURIComponent(selectedImage.path)}`)
          .then(r => r.json())
          .then(data => {
            if (data.cog_tci_url) {
              addTileLayer(data.cog_tci_url);
            } else {
              console.warn('Could not resolve COG URL:', data.error);
            }
          })
          .catch(err => console.error('Tile resolve failed:', err));
      }
      // DON'T return — fall through to classic image overlay for instant display + SR
    }

    // Save the current image reference for comparison
    const previousImage = currentImageRef.current;
    currentImageRef.current = selectedImage;

    // Skip transition if it's the first image or no image
    if (!selectedImage) {
      // Clean up any existing layers
      if (imageLayerRef.current) {
        imageLayerRef.current.forEach(id => {
          if (map.current.getLayer(id)) {
            map.current.removeLayer(id);
          }
        });
        imageLayerRef.current = null;
      }
      
      ['image-overlay', 'temp-image-source', 'image-overlay-old'].forEach(source => {
        if (map.current.getSource(source)) {
          map.current.removeSource(source);
        }
      });
      return;
    }

    if (!selectedImage.bbox || !selectedImage.url || !selectedImage.center || !selectedImage.zoom) {
      console.error('Selected image data is incomplete:', selectedImage);
      return;
    }

    // If it's the same image, don't do anything
    // But allow updates if super-resolution status changed, if lastUpdated timestamp is different (hover events),
    // or if image view mode changed
    if (previousImage && 
        previousImage.url === selectedImage.url && 
        previousImage.bbox[0] === selectedImage.bbox[0] &&
        previousImage.superresolved === selectedImage.superresolved &&
        previousImage.lastUpdated === selectedImage.lastUpdated &&
        previousImageViewModeRef.current === imageViewMode) {
      console.log('Skipping image update - same image and timestamp');
      return;
    }
    console.log('Updating image - different image or timestamp or view mode state', {
      viewModeChanged: previousImageViewModeRef.current !== imageViewMode,
      previousViewMode: previousImageViewModeRef.current,
      currentViewMode: imageViewMode
    });

    const bounds = [
      [selectedImage.bbox[0], selectedImage.bbox[3]], // top-left (west, north)
      [selectedImage.bbox[2], selectedImage.bbox[3]], // top-right (east, north)
      [selectedImage.bbox[2], selectedImage.bbox[1]], // bottom-right (east, south)
      [selectedImage.bbox[0], selectedImage.bbox[1]], // bottom-left (west, south)
    ];

    // Save the bounds for click detection
    imageBoundsRef.current = [
      selectedImage.bbox[0], // west
      selectedImage.bbox[1], // south
      selectedImage.bbox[2], // east
      selectedImage.bbox[3], // north
    ];

    // Initialize the layer tracking array if needed
    if (!imageLayerRef.current) {
      imageLayerRef.current = [];
    }

    // Determine which URL to use based on imageViewMode
    let imageUrl = selectedImage.url; // Default to natural color
    if (imageViewMode === 'evi' && selectedImage.evi_url) {
      imageUrl = selectedImage.evi_url;
    } else if (imageViewMode === 'false_color' && selectedImage.false_color_url) {
      imageUrl = selectedImage.false_color_url;
    }
    
    console.log('Image URL selection:', {
      imageViewMode,
      has_false_color_url: !!selectedImage.false_color_url,
      false_color_url: selectedImage.false_color_url,
      has_evi_url: !!selectedImage.evi_url,
      evi_url: selectedImage.evi_url,
      regular_url: selectedImage.url,
      using_url: imageUrl
    });
    
    // Check if image exists when switching to different view modes
    if (imageViewMode === 'false_color' && selectedImage.false_color_url) {
      // Create an image element to test if the false color image loads
      const testImg = new Image();
      testImg.onerror = () => {
        console.warn('False color image not available, falling back to regular image');
        // Could implement a fallback here, but for now just log the warning
      };
      testImg.src = selectedImage.false_color_url;
    } else if (imageViewMode === 'evi' && selectedImage.evi_url) {
      // Create an image element to test if the EVI image loads
      const testImg = new Image();
      testImg.onerror = () => {
        console.warn('EVI image not available, falling back to regular image');
        // Could implement a fallback here, but for now just log the warning
      };
      testImg.src = selectedImage.evi_url;
    }

    // Improved transition approach without flickering
    try {
      
      // If there's no previous image, just add the new one directly
      if (!map.current.getLayer('image-layer')) {
        // Add the new image source and layer directly
        map.current.addSource('image-overlay', {
          type: 'image',
          url: imageUrl,
          coordinates: bounds,
        });
        
        map.current.addLayer({
          id: 'image-layer',
          type: 'raster',
          source: 'image-overlay',
          paint: { 
            'raster-opacity': 0,
            'raster-fade-duration': 0
          }
        });
        
        // Fade in the new image
        requestAnimationFrame(() => {
          if (map.current && map.current.getLayer('image-layer')) {
            map.current.setPaintProperty('image-layer', 'raster-fade-duration', 500);
            map.current.setPaintProperty('image-layer', 'raster-opacity', 1);
          }
        });
        
        imageLayerRef.current = ['image-layer'];
      } else {
        // Crossfade between images - keep current image visible while loading new one
        // Clean up old temporary layers first (but not the current image-layer)
        if (imageLayerRef.current && imageLayerRef.current.length > 1) {
          imageLayerRef.current
            .filter(id => id !== 'image-layer' && map.current.getLayer(id))
            .forEach(id => {
              try {
                map.current.removeLayer(id);
                const sourceId = id.replace('-layer-', '-source-');
                if (map.current.getSource(sourceId)) {
                  map.current.removeSource(sourceId);
                }
              } catch (e) {
                // Layer might already be removed
              }
            });
        }
        
        const timestamp = Date.now();
        const tempSourceId = `temp-image-source-${timestamp}`;
        const tempLayerId = `temp-image-layer-${timestamp}`;
        
        // First, create the new source
        map.current.addSource(tempSourceId, {
          type: 'image',
          url: imageUrl,
          coordinates: bounds,
        });
        
        // Add the new layer BELOW the current one with full opacity
        const currentLayerId = imageLayerRef.current && imageLayerRef.current[0];
        map.current.addLayer({
          id: tempLayerId,
          type: 'raster',
          source: tempSourceId,
          layout: {
            visibility: 'visible'
          },
          paint: {
            'raster-opacity': 1,
            'raster-fade-duration': 0
          }
        }, currentLayerId || 'image-layer'); // Add below the current image layer
        
        // Create an image element to detect when the image is loaded
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        // Simplified cleanup function - avoid complex renaming to prevent flicker
        const cleanupAndSwapLayers = () => {
          try {
            // Remove the old faded-out layer and its source
            const oldLayerId = imageLayerRef.current && imageLayerRef.current[0];
            if (oldLayerId && map.current.getLayer(oldLayerId)) {
              map.current.removeLayer(oldLayerId);
              
              // Remove the associated source
              if (oldLayerId === 'image-layer') {
                if (map.current.getSource('image-overlay')) {
                  map.current.removeSource('image-overlay');
                }
              } else if (window._imageSourceMap && window._imageSourceMap[oldLayerId]) {
                const oldSourceId = window._imageSourceMap[oldLayerId];
                if (map.current.getSource(oldSourceId)) {
                  map.current.removeSource(oldSourceId);
                }
                delete window._imageSourceMap[oldLayerId];
              }
            }
            
            // Simply use the temp layer as our new active layer (no renaming to avoid flicker)
            imageLayerRef.current = [tempLayerId];
            
            // Store the source ID so we know which source belongs to this layer
            if (!window._imageSourceMap) {
              window._imageSourceMap = {};
            }
            window._imageSourceMap[tempLayerId] = tempSourceId;
            
            console.log('Layer cleanup complete - using temp layer:', tempLayerId);
          } catch (innerError) {
            console.error('Error during layer cleanup:', innerError);
            imageLayerRef.current = [tempLayerId];
          }
        };
        
        img.onload = () => {
          // Image is loaded, now fade out the top layer
          requestAnimationFrame(() => {
            if (!map.current) return;
            
            // Simply fade out the old image to reveal the new one underneath
            // The old image might be 'image-layer' or a temp layer from previous transition
            const oldLayerId = imageLayerRef.current && imageLayerRef.current[0];
            if (oldLayerId && map.current.getLayer(oldLayerId)) {
              map.current.setPaintProperty(oldLayerId, 'raster-fade-duration', 400);
              map.current.setPaintProperty(oldLayerId, 'raster-opacity', 0);
            }
            
            // After fade completes, clean up
            setTimeout(() => {
              if (!map.current) return;
              cleanupAndSwapLayers();
            }, 450); // Wait for fade to complete
          });
        };
        
        // Handle load error
        img.onerror = () => {
          console.error('Failed to load image:', selectedImage.url);
          // On error, still try to transition
          requestAnimationFrame(() => {
            if (!map.current) return;
            
            // Fade out old image anyway
            const oldLayerId = imageLayerRef.current && imageLayerRef.current[0];
            if (oldLayerId && map.current.getLayer(oldLayerId)) {
              map.current.setPaintProperty(oldLayerId, 'raster-fade-duration', 400);
              map.current.setPaintProperty(oldLayerId, 'raster-opacity', 0);
            }
            
            setTimeout(() => {
              if (!map.current) return;
              cleanupAndSwapLayers();
            }, 450);
          });
        };
        
        // Start loading the image
        img.src = selectedImage.url;
      }
    } catch (error) {
      console.error('Error updating image with smooth transition:', error);
      
      // Fallback in case of error - direct replacement
      try {
        // Clean up any existing layers first
        if (imageLayerRef.current) {
          imageLayerRef.current.forEach(id => {
            if (map.current.getLayer(id)) {
              map.current.removeLayer(id);
            }
          });
        }
        
        // Clean up old sources
        ['image-overlay', 'temp-image-source', 'new-image-source'].forEach(source => {
          if (map.current.getSource(source)) {
            map.current.removeSource(source);
          }
        });
        
        // Add new source and layer directly
        map.current.addSource('image-overlay', {
          type: 'image',
          url: imageUrl,
          coordinates: bounds,
        });
        
        map.current.addLayer({
          id: 'image-layer',
          type: 'raster',
          source: 'image-overlay',
          paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 }
        });
        
        // Update layer tracking
        imageLayerRef.current = ['image-layer'];
      } catch (fallbackError) {
        console.error('Error in fallback image display:', fallbackError);
      }
    }

    // Add click event listener to the map
    map.current.on('click', handleImageClick);

    // Update the previous image view mode state after all updates are done
    previousImageViewModeRef.current = imageViewMode;
  }, [selectedImage, mapLoaded, handleImageClick, imageViewMode, tileMode]);

  // Toggle between tile mode and classic mode
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (!tileMode) {
      // Turning off tiles: remove tile layer, classic stays visible
      if (map.current.getLayer('s2-tile-layer')) {
        map.current.removeLayer('s2-tile-layer');
        map.current.removeSource('s2-tiles');
      }
    }
    // When turning on: tiles are added by the selectedImage useEffect
  }, [tileMode, mapLoaded]);

  return (
    <div ref={mapContainer} style={{
      width: '100%',
      // Use dynamic viewport height on mobile for better browser compatibility
      height: '100dvh',
      position: 'relative'
    }}>
      {showRedBox && (
        <div
          ref={redBoxRef}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            border: '2px solid red',
            pointerEvents: 'none',
            zIndex: 1000,
            transition: 'width 0.15s ease-out, height 0.15s ease-out',
            // Mobile centering adjustment
            ...(window.innerWidth <= 768 && {
              top: 'calc(50% - 2.5vh)', // Adjust for mobile viewport issues
            }),
          }}
        >
          <div
            ref={resizeHandleRef}
            style={{
              position: 'absolute',
              bottom: '-8px',
              right: '-8px',
              width: '16px',
              height: '16px',
              backgroundColor: 'red',
              border: '2px solid white',
              borderRadius: '50%',
              cursor: 'nwse-resize',
              pointerEvents: 'all',
              zIndex: 1001,
            }}
            onMouseDown={startResizeDrag}
            onTouchStart={startResizeDrag}
          />
        </div>
      )}
      {mode === 'imageview' && selectedImage && (
        <>
          {/* Debug logging for download button conditions */}
          {console.log('MapComponent debug:', {
            mode,
            selectedImage: !!selectedImage,
            superresolved: selectedImage?.superresolved,
            superres_tif_url: selectedImage?.superres_tif_url,
            showButton: selectedImage?.superresolved && selectedImage?.superres_tif_url
          })}
          
          {/* Bottom-right button bar: GeoTIFF + Tiles + Color mode */}
          <div style={{
            position: 'absolute',
            bottom: '120px',
            right: '20px',
            zIndex: 1000,
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
          }}>
            {selectedImage.superresolved && selectedImage.superres_tif_url && (
              <button
                className="map-download-geotiff"
                onClick={() => {
                  let downloadUrl = selectedImage.superres_tif_url;
                  if (downloadUrl.startsWith('s3://')) {
                    const s3Parts = downloadUrl.replace('s3://', '').split('/', 1);
                    const bucket = s3Parts[0];
                    const key = downloadUrl.replace(`s3://${bucket}/`, '');
                    downloadUrl = `https://${bucket}.s3.eu-central-1.amazonaws.com/${key}`;
                  }
                  const center = map.current.getCenter();
                  Analytics.trackGeotiffDownloaded(
                    `${center.lat},${center.lng}`, selectedImage.date, 100000000, true
                  );
                  const bounds = map.current?.getBounds();
                  const currentBbox = bounds
                    ? [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
                    : null;
                  logFrontendEvent({
                    endpoint: '/download-geotiff', method: 'GET',
                    requestParams: { download_type: 'superres_tif', download_url: downloadUrl, image_path: selectedImage.path },
                    bbox: currentBbox, imageDate: selectedImage.date,
                  });
                  window.open(downloadUrl, '_blank');
                }}
                title="Download super-resolution GeoTIFF"
                style={{
                  backgroundColor: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6',
                  padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
                  transition: 'background-color 0.3s', display: 'flex', alignItems: 'center', gap: '6px',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#e9ecef'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#f8f9fa'; }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_downward</span>
                <span>GeoTIFF</span>
              </button>
            )}
            {onToggleTileMode && apiConfig.tileserver && (
              <button
                onClick={onToggleTileMode}
                title={tileMode ? 'Disable tile view' : 'Enable tile view'}
                style={{
                  backgroundColor: tileMode ? '#3b82f6' : 'rgba(255,255,255,0.85)',
                  color: tileMode ? 'white' : '#333',
                  border: tileMode ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.85)',
                  padding: '8px 12px', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '14px', transition: 'background-color 0.3s',
                }}
              >
                Tiles
              </button>
            )}
            <button
              className="map-image-view-toggle"
              onClick={onToggleImageView}
              title="Cycle through image view modes"
              style={{
                backgroundColor: imageViewMode === 'natural' ? '#ff8c00' : imageViewMode === 'evi' ? '#019c68' : '#dc3545',
                color: 'white',
                border: `1px solid ${imageViewMode === 'natural' ? '#ff8c00' : imageViewMode === 'evi' ? '#019c68' : '#dc3545'}`,
                padding: '8px 16px', borderRadius: '4px', cursor: 'pointer',
                fontSize: '14px', transition: 'background-color 0.3s',
              }}
              onMouseEnter={(e) => {
                const baseColor = imageViewMode === 'natural' ? '#ff8c00' : imageViewMode === 'evi' ? '#019c68' : '#dc3545';
                e.target.style.backgroundColor = baseColor;
              }}
              onMouseLeave={(e) => {
                const baseColor = imageViewMode === 'natural' ? '#ff8c00' : imageViewMode === 'evi' ? '#019c68' : '#dc3545';
                e.target.style.backgroundColor = baseColor;
              }}
            >
              {imageViewMode === 'natural' ? 'Natural Color' : imageViewMode === 'evi' ? 'EVI' : 'False Color'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default MapComponent;
